import {
    Alert,
    Autocomplete,
    Box,
    Checkbox,
    Chip,
    FormControlLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FlussElementSelection } from "modules/fluss/types/fluss";
import { LogicalNode } from "modules/record/record-producer/types/query.types";
import DurationInput from "../DurationInput";
import EventTypeSelect from "../EventTypeSelect";
import FieldMapper from "../FieldMapper";
import FlussConditionBuilder from "../FlussConditionBuilder";
import DataPicker from "../DataPicker";
import {
    DurationValue,
    FieldMapping,
    ScopeOption,
} from "../fluss-builder-types";
import FlussNodePanelField from "./FlussNodePanelField";
import FlussNodePanelShell from "./FlussNodePanelShell";
import PayloadVariableMapper, { SimpleMapRow } from "./PayloadVariableMapper";
import { FlussNodePanelData } from "./fluss-node-panel-data";
import {
    MESSAGE_START_EVENT_TYPE,
    durationToMinutes,
    messageBindingFor,
    messageEventTypeFor,
    metadataToScopeOptions,
    minutesToDuration,
    panelNodeKindFor,
    parseMapping,
    primaryBindingFor,
    resolveAnchorTable,
    serializeMapping,
    signalEventTypeFor,
    updateBinding,
    updateSignalEventType,
} from "./fluss-node-panel-utils";
import { useFlussTableColumns } from "./useFlussNodePanelMetadata";
import { renderTableOption, sortedTableOptions } from "./TableOption";
import { RecordSourceBindingField } from "./RecordSourceField";
import { NodeNameField } from "./ActivityNodePanels";
import { useDataCatalogContext } from "../data-picker/DataCatalogContext";

interface EventPanelProps {
    selection: FlussElementSelection;
    modeler: any;
    data: FlussNodePanelData;
    canViewAdvanced: boolean;
    readOnly: boolean;
    onChanged: () => void;
}

const parseSimpleMap = (raw: unknown): SimpleMapRow[] =>
    parseMapping(raw).map((row) => ({ target: row.target, source: String(row.source ?? "") }));

const serializeSimpleMap = (rows: SimpleMapRow[]) => {
    const complete = rows.filter((row) => row.target.trim() && row.source.trim());
    return complete.length
        ? JSON.stringify(Object.fromEntries(complete.map((row) => [row.target.trim(), row.source.trim()])))
        : "";
};

const EMAIL_CONDITION_FIELDS: ScopeOption[] = [
    { name: "from_address", label: "From address", type: "TEXT" },
    { name: "subject", label: "Subject", type: "TEXT" },
    { name: "body_text", label: "Body text", type: "TEXT" },
    { name: "to_addresses", label: "To addresses", type: "TEXT" },
    { name: "cc_addresses", label: "Cc addresses", type: "TEXT" },
    { name: "email_account_code", label: "Email account", type: "TEXT" },
    { name: "from_user_found", label: "Sender user found", type: "BOOLEAN" },
];

const EventTypeField = ({
    value,
    data,
    onChange,
    disabled,
}: {
    value: string;
    data: FlussNodePanelData;
    onChange: (eventType: string) => void;
    disabled: boolean;
}) => {
    const options = data.eventTypes.map((eventType) => ({
        id: eventType.id,
        name: eventType.name,
        label: eventType.label || eventType.name,
        active: eventType.active !== false,
    }));
    const current = options.find((option) => option.name === value || option.id === value);
    // PRIM-032 (round 9): EventTypeSelect owns the associated input label; no free-text wrapper.
    return (
        <EventTypeSelect
            value={current?.id || value || null}
            options={options}
            disabled={disabled}
            onChange={(id) => {
                const selected = options.find((option) => option.id === id);
                onChange(selected?.name || "");
            }}
        />
    );
};

export const MessageStartNodePanel = (props: EventPanelProps) => {
    const { t } = useTranslation();
    const { selection, modeler, data, readOnly, canViewAdvanced, onChanged } = props;
    const binding = messageBindingFor(selection.businessObject);
    const eventType = messageEventTypeFor(selection.businessObject);
    const [condition, setCondition] = useState<LogicalNode | string>(String(binding?.condition || ""));
    const [mappings, setMappings] = useState<SimpleMapRow[]>(parseSimpleMap(binding?.variableMappings));
    const eventTypeRecord = data.eventTypes.find((candidate) => candidate.name === eventType);
    const inactive = eventTypeRecord?.active === false;

    useEffect(() => {
        const current = messageBindingFor(selection.businessObject);
        setCondition(String(current?.condition || ""));
        setMappings(parseSimpleMap(current?.variableMappings));
    }, [selection.id, selection.businessObject]);

    const change = (updates: Record<string, unknown>) => {
        updateBinding(selection, modeler, "message", { eventType, ...updates });
        onChanged();
    };

    return (
        <FlussNodePanelShell
            selection={selection}
            kind="message_start"
            canViewAdvanced={canViewAdvanced}
            tabs={[{
                key: "event",
                label: t("fluss.node_panel.tab.event", { defaultValue: "Event" }),
                content: (
                    <Stack spacing={1.25}>
                        <TextField
                            size="small"
                            label={t("fluss.node_panel.message.event_type", { defaultValue: "Event type" })}
                            value={t("fluss.node_panel.message.email_received", { defaultValue: "Email received" })}
                            disabled
                            fullWidth
                        />
                        {canViewAdvanced && (
                            <Typography variant="caption" color="text.secondary" title={eventType || MESSAGE_START_EVENT_TYPE}>
                                {eventType || MESSAGE_START_EVENT_TYPE}
                            </Typography>
                        )}
                        {inactive && (
                            <Alert severity="warning">
                                {t("fluss.node_panel.message.inactive_event_type", {
                                    defaultValue: "No email starts this process until an admin activates this event type.",
                                })}
                            </Alert>
                        )}
                        <FlussNodePanelField
                            label={t("fluss.node_panel.message.trigger_condition", { defaultValue: "Trigger only if" })}
                            fieldPath="condition" issuePrefix
                        >
                            <FlussConditionBuilder
                                value={condition}
                                payloadFields={EMAIL_CONDITION_FIELDS}
                                readOnly={readOnly}
                                onChange={(next) => {
                                    setCondition(next);
                                    change({ condition: typeof next === "string" ? next : JSON.stringify(next) });
                                }}
                            />
                        </FlussNodePanelField>
                        <FlussNodePanelField
                            label={t("fluss.node_panel.message.initial_variables", { defaultValue: "Initial variables" })}
                            fieldPath="variableMappings" issuePrefix
                        >
                            <PayloadVariableMapper
                                value={mappings}
                                payloadKeys={EMAIL_CONDITION_FIELDS}
                                sourcePrefix="payload."
                                readOnly={readOnly}
                                onChange={(next) => {
                                    setMappings(next);
                                    change({ variableMappings: serializeSimpleMap(next) });
                                }}
                            />
                        </FlussNodePanelField>
                    </Stack>
                ),
            }]}
        />
    );
};

export const MessageCatchNodePanel = ({ selection, modeler, canViewAdvanced, readOnly, onChanged }: EventPanelProps) => {
    const { t } = useTranslation();
    const binding = messageBindingFor(selection.businessObject);
    const [condition, setCondition] = useState<LogicalNode | string>(String(binding?.condition || ""));
    const [mappings, setMappings] = useState<SimpleMapRow[]>(parseSimpleMap(binding?.variableMappings));
    const [replyToNode, setReplyToNode] = useState(String(binding?.replyToNode || ""));
    const sendTasks = (modeler?.get("elementRegistry")?.getAll() || [])
        .filter((element: any) => element.type === "bpmn:SendTask" && element.id !== selection.id);

    useEffect(() => {
        const current = messageBindingFor(selection.businessObject);
        setCondition(String(current?.condition || ""));
        setMappings(parseSimpleMap(current?.variableMappings));
        setReplyToNode(String(current?.replyToNode || ""));
    }, [selection.id, selection.businessObject]);

    const change = (updates: Record<string, unknown>) => {
        updateBinding(selection, modeler, "message", { eventType: MESSAGE_START_EVENT_TYPE, ...updates });
        onChanged();
    };

    return <FlussNodePanelShell selection={selection} kind="message_catch" canViewAdvanced={canViewAdvanced}
        tabs={[{ key: "event", label: t("fluss.node_panel.tab.event", { defaultValue: "Event" }), content: <Stack spacing={1.25}>
            <NodeNameField selection={selection} modeler={modeler} readOnly={readOnly} onChanged={onChanged} />
            {!replyToNode && <RecordSourceBindingField selection={selection} modeler={modeler} kind="message" readOnly={readOnly} onChanged={onChanged} />}
            <TextField select size="small" fullWidth disabled={readOnly}
                label={t("fluss.node_panel.message_catch.reply_to", { defaultValue: "In reply to" })}
                SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
                value={replyToNode} onChange={(event) => { setReplyToNode(event.target.value); change({ replyToNode: event.target.value }); }}>
                <MenuItem value="">{t("fluss.node_panel.message_catch.any_email", { defaultValue: "Any email associated with the record" })}</MenuItem>
                {sendTasks.map((task: any) => <MenuItem key={task.id} value={task.id}>{task.businessObject?.name || task.id}</MenuItem>)}
            </TextField>
            <FlussNodePanelField label={t("fluss.node_panel.message_catch.condition", { defaultValue: "Continue only if" })}
                fieldPath="condition" issuePrefix>
                <FlussConditionBuilder value={condition} payloadFields={EMAIL_CONDITION_FIELDS} readOnly={readOnly}
                    onChange={(next) => { setCondition(next); change({ condition: typeof next === "string" ? next : JSON.stringify(next) }); }} />
            </FlussNodePanelField>
            <FlussNodePanelField label={t("fluss.node_panel.message_catch.output_variables", { defaultValue: "Output variables" })}
                fieldPath="variableMappings" issuePrefix>
                <PayloadVariableMapper value={mappings} payloadKeys={EMAIL_CONDITION_FIELDS} sourcePrefix="payload."
                    readOnly={readOnly} onChange={(next) => { setMappings(next); change({ variableMappings: serializeSimpleMap(next) }); }} />
            </FlussNodePanelField>
            <Alert severity="info">{t("fluss.node_panel.message_catch.info", {
                defaultValue: "The process continues when an email associated with this record arrives: a reply to an email sent from it, or an email containing the platform identifier. For a deadline, use an event-based gateway with a timer.",
            })}</Alert>
        </Stack> }]}
    />;
};

export const SignalNodePanel = (props: EventPanelProps) => {
    const { t } = useTranslation();
    const { selection, modeler, data, canViewAdvanced, readOnly, onChanged } = props;
    const kind = panelNodeKindFor(selection);
    const binding = primaryBindingFor(selection.businessObject);
    const [eventType, setEventType] = useState(signalEventTypeFor(selection.businessObject));
    const [condition, setCondition] = useState<LogicalNode | string>(String(binding?.condition || ""));
    const [correlationKey, setCorrelationKey] = useState(String(binding?.correlationKey || ""));
    const [mapping, setMapping] = useState<FieldMapping[]>(parseMapping(
        kind === "signal_start" ? binding?.variableMappings : binding?.payload
    ));
    const [startMappings, setStartMappings] = useState<SimpleMapRow[]>(
        parseSimpleMap(binding?.variableMappings)
    );
    const [catchMappings, setCatchMappings] = useState<SimpleMapRow[]>(
        parseSimpleMap(binding?.variableMappings)
    );
    const [timeoutEnabled, setTimeoutEnabled] = useState(Boolean(binding?.timeoutMinutes));
    const [timeout, setTimeout] = useState<DurationValue>(
        minutesToDuration(binding?.timeoutMinutes)
    );
    const anchorTable = String(binding?.table || resolveAnchorTable(modeler) || "");
    const payloadTargets = useMemo(() => {
        const keys = new Set(mapping.map((row) => row.target).filter(Boolean));
        const example = data.eventTypes.find((candidate) => candidate.name === eventType)?.payload_example;
        try {
            const parsed = example ? JSON.parse(example) : {};
            if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
                Object.keys(parsed).forEach((key) => keys.add(key));
            }
        } catch (_error) {
            // Invalid catalog examples are ignored; publish validation remains the source of truth.
        }
        // PRIM-019 (round 6): "" (unknown), not "TEXT" — signal payload keys, no real type known
        // (the payload_example catalog entry has no per-key type, just an example value).
        return Array.from(keys).map((name) => ({ name, label: name, type: "" }));
    }, [data.eventTypes, eventType, mapping]);

    useEffect(() => {
        const current = primaryBindingFor(selection.businessObject);
        setEventType(signalEventTypeFor(selection.businessObject));
        setCondition(String(current?.condition || ""));
        setCorrelationKey(String(current?.correlationKey || ""));
        setMapping(parseMapping(kind === "signal_start" ? current?.variableMappings : current?.payload));
        setStartMappings(parseSimpleMap(current?.variableMappings));
        setCatchMappings(parseSimpleMap(current?.variableMappings));
        setTimeoutEnabled(Boolean(current?.timeoutMinutes));
        setTimeout(minutesToDuration(current?.timeoutMinutes));
    }, [kind, selection.id, selection.businessObject]);

    const change = (updates: Record<string, unknown>) => {
        updateBinding(selection, modeler, "signal", updates);
        onChanged();
    };
    const eventField = (
        <EventTypeField
            value={eventType}
            data={data}
            disabled={readOnly}
            onChange={(next) => {
                setEventType(next);
                updateSignalEventType(selection, modeler, next);
                onChanged();
            }}
        />
    );

    let body;
    if (kind === "signal_start") {
        body = (
            <Stack spacing={1.25}>
                {eventField}
                <FlussNodePanelField
                    label={t("fluss.node_panel.signal.trigger_condition", { defaultValue: "Trigger only if" })}
                    fieldPath="condition" issuePrefix
                >
                    <FlussConditionBuilder
                        value={condition}
                        payloadFields={payloadTargets.map((field) => ({ ...field, type: "TEXT" }))}
                        readOnly={readOnly}
                        onChange={(next) => {
                            setCondition(next);
                            change({ condition: typeof next === "string" ? next : JSON.stringify(next) });
                        }}
                    />
                </FlussNodePanelField>
                <FlussNodePanelField
                    label={t("fluss.node_panel.signal.initial_variables", {
                        defaultValue: "Initial variables",
                    })}
                    fieldPath="variableMappings" issuePrefix
                >
                    <PayloadVariableMapper
                        value={startMappings}
                        payloadKeys={payloadTargets}
                        sourcePrefix="payload."
                        readOnly={readOnly}
                        onChange={(next) => {
                            setStartMappings(next);
                            change({ variableMappings: serializeSimpleMap(next) });
                        }}
                    />
                </FlussNodePanelField>
            </Stack>
        );
    } else if (kind === "signal_catch" || kind === "boundary_signal" || kind === "event_start_signal") {
        body = (
            <Stack spacing={1.25}>
                {eventField}
                <FlussNodePanelField
                    label={t("fluss.node_panel.signal.match_field", { defaultValue: "Match by field" })}
                    fieldPath="correlationKey"
                >
                    <DataPicker
                        value={correlationKey || undefined}
                        onChange={(next) => {
                            // PRIM-020: undefined means "cleared" -- stringifying it first would
                            // have written the literal text "undefined" to the binding.
                            const serialized = next === undefined ? "" : String(next);
                            setCorrelationKey(serialized);
                            change({ correlationKey: serialized });
                        }}
                        recordTable={anchorTable || undefined}
                        allowFixed={false}
                        disabled={readOnly}
                    />
                </FlussNodePanelField>
                {(kind === "signal_catch" || kind === "boundary_signal") && (
                    <FlussNodePanelField
                        label={t("fluss.node_panel.signal.received_variables", {
                            defaultValue: "Received variables",
                        })}
                        fieldPath="variableMappings" issuePrefix
                    >
                        <PayloadVariableMapper
                            value={catchMappings}
                            payloadKeys={payloadTargets}
                            sourcePrefix="payload."
                            readOnly={readOnly}
                            onChange={(next) => {
                                setCatchMappings(next);
                                change({ variableMappings: serializeSimpleMap(next) });
                            }}
                        />
                    </FlussNodePanelField>
                )}
                {kind !== "event_start_signal" && <FormControlLabel
                    control={(
                        <Checkbox
                            checked={timeoutEnabled}
                            disabled={readOnly}
                            onChange={(event) => {
                                setTimeoutEnabled(event.target.checked);
                                change({
                                    timeoutMinutes: event.target.checked
                                        ? durationToMinutes(timeout.amount, timeout.unit)
                                        : "",
                                });
                            }}
                        />
                    )}
                    label={t("fluss.node_panel.signal.maximum_wait", {
                        defaultValue: "Maximum wait time",
                    })}
                />}
                {kind !== "event_start_signal" && timeoutEnabled && (
                    <DurationInput
                        value={timeout}
                        disabled={readOnly}
                        onChange={(next) => {
                            setTimeout(next);
                            change({ timeoutMinutes: durationToMinutes(next.amount, next.unit) });
                        }}
                    />
                )}
                {kind !== "event_start_signal" && <Typography variant="caption" color="text.secondary">
                    {t("fluss.node_panel.signal.timeout_help", {
                        defaultValue: "When it expires, the process follows the timeout output drawn on the canvas.",
                    })}
                </Typography>}
            </Stack>
        );
    } else {
        body = (
            <Stack spacing={1.25}>
                <RecordSourceBindingField selection={selection} modeler={modeler} kind="signal" readOnly={readOnly} onChanged={onChanged} />
                {eventField}
                <FlussNodePanelField
                    label={t("fluss.node_panel.signal.payload", { defaultValue: "Payload" })} fieldPath="payload"
                >
                    {/* PRIM-015 (round 6, call site #4): targets are payload keys (variables),
                        not columns of a record being created — never enforce. */}
                    <FieldMapper
                        value={mapping}
                        targetFields={payloadTargets}
                        recordTable={anchorTable || undefined}
                        disabled={readOnly}
                        enforceRequired={false}
                        onChange={(next) => {
                            setMapping(next);
                            change({ payload: serializeMapping(next) });
                        }}
                    />
                </FlussNodePanelField>
                <FlussNodePanelField
                    label={t("fluss.node_panel.signal.correlation_key", {
                        defaultValue: "Correlation key",
                    })}
                    fieldPath="correlationKey"
                >
                    <DataPicker
                        value={correlationKey || undefined}
                        onChange={(next) => {
                            // PRIM-020: undefined means "cleared" -- stringifying it first would
                            // have written the literal text "undefined" to the binding.
                            const serialized = next === undefined ? "" : String(next);
                            setCorrelationKey(serialized);
                            change({ correlationKey: serialized });
                        }}
                        recordTable={anchorTable || undefined}
                        allowFixed={false}
                        disabled={readOnly}
                    />
                </FlussNodePanelField>
            </Stack>
        );
    }

    return (
        <FlussNodePanelShell
            selection={selection}
            kind={kind}
            canViewAdvanced={canViewAdvanced}
            tabs={[{
                key: "event",
                label: t("fluss.node_panel.tab.event", { defaultValue: "Event" }),
                content: body,
            }]}
        />
    );
};

// PRIM-029 (round 8, Bloco E): boundary_signal is a catch attached to an activity -- same shape
// as signal_catch (match by field + optional timeout), reusing SignalNodePanel's branch as-is
// instead of a parallel implementation.
export const BoundarySignalNodePanel = SignalNodePanel;

const browserTimezone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
        return "UTC";
    }
};

const IANA_TIMEZONES: string[] = Array.from(new Set([
    "UTC",
    browserTimezone(),
    ...((Intl as any).supportedValuesOf?.("timeZone") || [
        "America/Sao_Paulo",
        "America/New_York",
        "Europe/London",
    ]),
]));

const WEEK_DAYS = [
    [0, "sun", "Sun"],
    [1, "mon", "Mon"],
    [2, "tue", "Tue"],
    [3, "wed", "Wed"],
    [4, "thu", "Thu"],
    [5, "fri", "Fri"],
    [6, "sat", "Sat"],
] as const;

const MONTH_DAYS = Array.from({ length: 31 }, (_, index) => index + 1);
type MonthDay = number | "last";

const weeklyDays = (raw: unknown, time: string): number[] => {
    const days = String(raw || "")
        .split(",")
        .filter((value) => value.trim() !== "")
        .map((value) => Number(value))
        .filter((value) => Number.isInteger(value) && value >= 0 && value <= 6);
    if (days.length) return [...new Set(days)].sort((a, b) => a - b);

    const parsed = Date.parse(time);
    return Number.isNaN(parsed) ? [] : [new Date(parsed).getUTCDay()];
};

const weeklyTime = (value: unknown) => String(value || "").match(/\d{1,2}:\d{2}/)?.[0] || "";

const sortMonthDays = (days: MonthDay[]) =>
    [...new Set(days)].sort((a, b) => (a === "last" ? 32 : a) - (b === "last" ? 32 : b));

const monthlyDays = (raw: unknown, time: string): MonthDay[] => {
    const days = String(raw || "")
        .split(",")
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean)
        .map((value): MonthDay | null => {
            if (value === "last") return "last";
            const day = Number(value);
            return Number.isInteger(day) && day >= 1 && day <= 31 ? day : null;
        })
        .filter((value): value is MonthDay => value !== null);
    if (days.length) return sortMonthDays(days);

    const parsed = Date.parse(time);
    return Number.isNaN(parsed) ? [] : [new Date(parsed).getDate()];
};

const nextCronPreview = (expression: string, timezone: string) => {
    const match = expression.trim().match(/^(\d{1,2})\s+(\d{1,2})\s+\*\s+\*\s+\*$/);
    if (!match) return "";
    const minute = Number(match[1]);
    const hour = Number(match[2]);
    if (minute > 59 || hour > 23) return "";
    const next = new Date();
    next.setUTCSeconds(0, 0);
    next.setUTCHours(hour, minute, 0, 0);
    if (next <= new Date()) next.setUTCDate(next.getUTCDate() + 1);
    try {
        return new Intl.DateTimeFormat(undefined, {
            dateStyle: "medium",
            timeStyle: "short",
            timeZone: timezone || "UTC",
        }).format(next);
    } catch (_error) {
        return "";
    }
};

export const TimerNodePanel = (props: EventPanelProps) => {
    const { t } = useTranslation();
    const { selection, modeler, data, canViewAdvanced, readOnly, onChanged } = props;
    const kind = panelNodeKindFor(selection);
    const binding = primaryBindingFor(selection.businessObject);
    const [scheduleType, setScheduleType] = useState(String(binding?.scheduleType || "daily"));
    const [timeOfDay, setTimeOfDay] = useState(["weekly", "monthly"].includes(scheduleType) ? weeklyTime(binding?.timeOfDay) : String(binding?.timeOfDay || ""));
    const [daysOfWeek, setDaysOfWeek] = useState<number[]>(weeklyDays(binding?.daysOfWeek, String(binding?.timeOfDay || "")));
    const [daysOfMonth, setDaysOfMonth] = useState<MonthDay[]>(monthlyDays(binding?.daysOfMonth, String(binding?.timeOfDay || "")));
    const [timezone, setTimezone] = useState(String(binding?.timezone || browserTimezone()));
    const [cron, setCron] = useState(String(binding?.cron || ""));
    const [mode, setMode] = useState(String(binding?.mode || "duration"));
    const [duration, setDuration] = useState<DurationValue>(minutesToDuration(binding?.durationMinutes));
    const [date, setDate] = useState(String(binding?.date || ""));
    const [field, setField] = useState(String(binding?.field || ""));
    const [offset, setOffset] = useState<DurationValue>(minutesToDuration(binding?.offsetMinutes));
    const [offsetDirection, setOffsetDirection] = useState(String(binding?.offsetDirection || "before"));

    useEffect(() => {
        const current = primaryBindingFor(selection.businessObject);
        const nextScheduleType = String(current?.scheduleType || "daily");
        setScheduleType(nextScheduleType);
        setTimeOfDay(["weekly", "monthly"].includes(nextScheduleType) ? weeklyTime(current?.timeOfDay) : String(current?.timeOfDay || ""));
        setDaysOfWeek(weeklyDays(current?.daysOfWeek, String(current?.timeOfDay || "")));
        setDaysOfMonth(monthlyDays(current?.daysOfMonth, String(current?.timeOfDay || "")));
        setTimezone(String(current?.timezone || browserTimezone()));
        setCron(String(current?.cron || ""));
        setMode(String(current?.mode || "duration"));
        setDuration(minutesToDuration(current?.durationMinutes));
        setDate(String(current?.date || ""));
        setField(String(current?.field || ""));
        setOffset(minutesToDuration(current?.offsetMinutes));
        setOffsetDirection(String(current?.offsetDirection || "before"));
    }, [selection.id, selection.businessObject]);

    const change = (updates: Record<string, unknown>) => {
        const current = primaryBindingFor(selection.businessObject);
        updateBinding(selection, modeler, "timer",
            (kind === "timer_start" || (kind === "timer_catch" && (updates.mode === "date" || mode === "date"))) && !current?.timezone && !("timezone" in updates)
                ? { timezone, ...updates }
                : updates);
        onChanged();
    };

    const startBody = (
        <Stack spacing={1.25}>
            <FlussNodePanelField label={t("fluss.node_panel.timer.schedule", { defaultValue: "Schedule" })}>
                <TextField
                    select
                    size="small"
                    value={scheduleType}
                    disabled={readOnly}
                    onChange={(event) => {
                        const next = event.target.value;
                        setScheduleType(next);
                        const nextWeekDays = daysOfWeek.length ? daysOfWeek : [new Date().getDay()];
                        const nextMonthDays = daysOfMonth.length ? daysOfMonth : [new Date().getDate()];
                        if (next === "weekly") setDaysOfWeek(nextWeekDays);
                        if (next === "monthly") setDaysOfMonth(nextMonthDays);
                        change({
                            scheduleType: next,
                            cron: next === "cron" ? cron : "",
                            timeOfDay: next === "cron" ? "" : timeOfDay,
                            daysOfWeek: next === "weekly" ? nextWeekDays.join(",") : "",
                            daysOfMonth: next === "monthly" ? nextMonthDays.join(",") : "",
                        });
                    }}
                >
                    {[
                        ["once", "Once"],
                        ["daily", "Daily"],
                        ["weekly", "Weekly"],
                        ["monthly", "Monthly"],
                        ["cron", "Cron"],
                    ].map(([value, fallback]) => (
                        <MenuItem key={value} value={value}>
                            {t(`fluss.node_panel.timer.schedule.${value}`, { defaultValue: fallback })}
                        </MenuItem>
                    ))}
                </TextField>
            </FlussNodePanelField>
            {scheduleType === "weekly" && (
                <>
                    <FlussNodePanelField label={t("fluss.node_panel.timer.days_of_week", { defaultValue: "Days" })}>
                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                            {WEEK_DAYS.map(([value, key, fallback]) => (
                                <FormControlLabel
                                    key={value}
                                    control={(
                                        <Checkbox
                                            size="small"
                                            checked={daysOfWeek.includes(value)}
                                            disabled={readOnly}
                                            onChange={() => {
                                                const next = daysOfWeek.includes(value)
                                                    ? daysOfWeek.filter((day) => day !== value)
                                                    : [...daysOfWeek, value].sort((a, b) => a - b);
                                                if (!next.length) return;
                                                setDaysOfWeek(next);
                                                change({ daysOfWeek: next.join(","), timeOfDay, cron: "" });
                                            }}
                                        />
                                    )}
                                    label={t(`fluss.node_panel.timer.day.${key}`, { defaultValue: fallback })}
                                />
                            ))}
                        </Stack>
                    </FlussNodePanelField>
                </>
            )}
            {scheduleType === "monthly" && (
                <>
                    <FlussNodePanelField label={t("fluss.node_panel.timer.days_of_month", { defaultValue: "Days of month" })}>
                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                            {MONTH_DAYS.map((value) => (
                                <FormControlLabel
                                    key={value}
                                    control={(
                                        <Checkbox
                                            size="small"
                                            checked={daysOfMonth.includes(value)}
                                            disabled={readOnly}
                                            onChange={() => {
                                                const next = daysOfMonth.includes(value)
                                                    ? daysOfMonth.filter((day) => day !== value)
                                                    : sortMonthDays([...daysOfMonth, value]);
                                                if (!next.length) return;
                                                setDaysOfMonth(next);
                                                change({ daysOfMonth: next.join(","), daysOfWeek: "", timeOfDay, cron: "" });
                                            }}
                                        />
                                    )}
                                    label={value === 1
                                        ? t("fluss.node_panel.timer.month_day.first", { defaultValue: "1 · First day" })
                                        : String(value)}
                                />
                            ))}
                        </Stack>
                    </FlussNodePanelField>
                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={daysOfMonth.includes("last")}
                                disabled={readOnly}
                                onChange={() => {
                                    const next = daysOfMonth.includes("last")
                                        ? daysOfMonth.filter((day) => day !== "last")
                                        : sortMonthDays([...daysOfMonth, "last"]);
                                    if (!next.length) return;
                                    setDaysOfMonth(next);
                                    change({ daysOfMonth: next.join(","), daysOfWeek: "", timeOfDay, cron: "" });
                                }}
                            />
                        )}
                        label={t("fluss.node_panel.timer.month_day.last", { defaultValue: "Last day" })}
                    />
                    {String(binding?.daysOfMonth || "").trim() === "" && daysOfMonth.some((day) => typeof day === "number" && day >= 29) && (
                        <Typography variant="caption" color="text.secondary">
                            {t("fluss.node_panel.timer.month_day.short_month_help", {
                                defaultValue: "This day does not exist in every month. Use Last day for monthly closing.",
                            })}
                        </Typography>
                    )}
                </>
            )}
            <Stack direction={scheduleType === "once" || scheduleType === "cron" ? "column" : "row"} spacing={1} alignItems="stretch">
                {scheduleType !== "cron" && (
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <FlussNodePanelField
                            label={scheduleType !== "once"
                                ? t("fluss.node_panel.timer.time", { defaultValue: "Time" })
                                : t("fluss.node_panel.timer.date_time", { defaultValue: "Date and time" })}
                        >
                            <TextField
                                size="small"
                                type={scheduleType === "once" ? "datetime-local" : "time"}
                                value={timeOfDay}
                                disabled={readOnly}
                                onChange={(event) => {
                                    setTimeOfDay(event.target.value);
                                    change({
                                        timeOfDay: event.target.value,
                                        daysOfWeek: scheduleType === "weekly" ? daysOfWeek.join(",") : "",
                                        daysOfMonth: scheduleType === "monthly" ? daysOfMonth.join(",") : "",
                                        cron: "",
                                    });
                                }}
                            />
                        </FlussNodePanelField>
                    </Box>
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <FlussNodePanelField label={t("fluss.node_panel.timer.timezone", { defaultValue: "Time zone" })}>
                        <Autocomplete
                            size="small"
                            options={IANA_TIMEZONES}
                            value={timezone}
                            disabled={readOnly}
                            onChange={(_, value) => {
                                const next = value || "UTC";
                                setTimezone(next);
                                change({ timezone: next });
                            }}
                            renderInput={(params) => <TextField {...params} />}
                        />
                    </FlussNodePanelField>
                </Box>
            </Stack>
            {scheduleType === "cron" && (
                <FlussNodePanelField
                    label={t("fluss.node_panel.timer.cron", { defaultValue: "Cron expression" })}
                    hint={nextCronPreview(cron, timezone)
                        ? t("fluss.node_panel.timer.next_run", {
                            defaultValue: "Next execution: {{date}}",
                            date: nextCronPreview(cron, timezone),
                        })
                        : t("fluss.node_panel.timer.cron_help", {
                            defaultValue: "Use a valid five-field cron expression to preview the next execution.",
                        })}
                >
                    <TextField
                        size="small"
                        value={cron}
                        disabled={readOnly}
                        onChange={(event) => {
                            setCron(event.target.value);
                            change({ cron: event.target.value, timeOfDay: "" });
                        }}
                    />
                </FlussNodePanelField>
            )}
        </Stack>
    );

    const catchBody = (
        <Stack spacing={1.25}>
            <FlussNodePanelField label={t("fluss.node_panel.timer.mode", { defaultValue: "Mode" })}>
                <TextField
                    select
                    size="small"
                    value={mode}
                    disabled={readOnly}
                    onChange={(event) => {
                        const next = event.target.value;
                        setMode(next);
                        change({
                            mode: next,
                            durationMinutes: next === "duration" ? durationToMinutes(duration.amount, duration.unit) : "",
                            date: next === "date" ? date : "",
                            field: next === "field" ? field : "",
                            offsetMinutes: next === "field" ? durationToMinutes(offset.amount, offset.unit) : "",
                            offsetDirection: next === "field" ? offsetDirection : "",
                        });
                    }}
                >
                    <MenuItem value="duration">
                        {t("fluss.node_panel.timer.mode.duration", { defaultValue: "Duration" })}
                    </MenuItem>
                    <MenuItem value="date">
                        {t("fluss.node_panel.timer.mode.date", { defaultValue: "Fixed date" })}
                    </MenuItem>
                    <MenuItem value="field">
                        {t("fluss.node_panel.timer.mode.field", { defaultValue: "Field date" })}
                    </MenuItem>
                </TextField>
            </FlussNodePanelField>
            {mode === "duration" && (
                <DurationInput
                    value={duration}
                    disabled={readOnly}
                    onChange={(next) => {
                        setDuration(next);
                        change({ mode: "duration", durationMinutes: durationToMinutes(next.amount, next.unit), date: "", field: "" });
                    }}
                />
            )}
            {mode === "date" && (
                <Stack spacing={1}>
                <TextField
                    size="small"
                    type="datetime-local"
                    value={date}
                    disabled={readOnly}
                    onChange={(event) => {
                        setDate(event.target.value);
                        change({ mode: "date", date: event.target.value, durationMinutes: "", field: "" });
                    }}
                />
                <FlussNodePanelField label={t("fluss.node_panel.timer.timezone", { defaultValue: "Time zone" })}>
                    <Autocomplete
                        size="small"
                        options={IANA_TIMEZONES}
                        value={timezone}
                        disabled={readOnly}
                        onChange={(_, value) => {
                            const next = value || "UTC";
                            setTimezone(next);
                            change({ timezone: next });
                        }}
                        renderInput={(params) => <TextField {...params} />}
                    />
                </FlussNodePanelField>
                </Stack>
            )}
            {mode === "field" && (
                <>
                    <FlussNodePanelField label={t("fluss.node_panel.timer.mode.field", { defaultValue: "Field date" })} fieldPath="field">
                    <DataPicker
                        value={field || undefined}
                        onChange={(next) => {
                            // PRIM-020: undefined means "cleared" -- stringifying it first would
                            // have written the literal text "undefined" to the binding.
                            const serialized = next === undefined ? "" : String(next);
                            setField(serialized);
                            change({ mode: "field", field: serialized, durationMinutes: "", date: "" });
                        }}
                        allowedTypes={["DATE", "DATETIME"]}
                        allowFixed={false}
                        helperText={String(t("fluss.node_panel.timer.field_help", { defaultValue: "Choose a date value." }))}
                        disabled={readOnly}
                    />
                    </FlussNodePanelField>
                    <FlussNodePanelField
                        label={t("fluss.node_panel.timer.offset", { defaultValue: "Offset" })}
                    >
                        <DurationInput
                            value={offset}
                            disabled={readOnly}
                            onChange={(next) => {
                                setOffset(next);
                                change({
                                    offsetMinutes: durationToMinutes(next.amount, next.unit),
                                    offsetDirection,
                                });
                            }}
                        />
                        <Select
                            size="small"
                            value={offsetDirection}
                            disabled={readOnly}
                            onChange={(event) => {
                                setOffsetDirection(event.target.value);
                                change({
                                    offsetMinutes: durationToMinutes(offset.amount, offset.unit),
                                    offsetDirection: event.target.value,
                                });
                            }}
                        >
                            <MenuItem value="before">
                                {t("fluss.node_panel.timer.before", { defaultValue: "Before" })}
                            </MenuItem>
                            <MenuItem value="after">
                                {t("fluss.node_panel.timer.after", { defaultValue: "After" })}
                            </MenuItem>
                        </Select>
                    </FlussNodePanelField>
                </>
            )}
        </Stack>
    );

    return (
        <FlussNodePanelShell
            selection={selection}
            kind={kind}
            canViewAdvanced={canViewAdvanced}
            tabs={[{
                key: "timer",
                label: t("fluss.node_panel.tab.timer", { defaultValue: "Timer" }),
                content: <Stack spacing={1.25}>
                    {kind === "timer_start" ? startBody : catchBody}
                    <Typography variant="caption" color="text.secondary">
                        {t("fluss.node_panel.timer.resolution_help", { defaultValue: "Minimum resolution: 1 minute." })}
                    </Typography>
                </Stack>,
            }]}
        />
    );
};

// The raw stored duration_minutes alone can't distinguish "60 wall-clock minutes" from "60
// business-hours minutes" (both persist identically); the businessHours flag on the binding is
// the only source of truth for which unit DurationInput should show on load.
const boundaryTimerDurationFor = (binding: ReturnType<typeof primaryBindingFor>) => {
    const duration = minutesToDuration(binding?.durationMinutes);
    if (String(binding?.businessHours) === "true") return { ...duration, unit: "business_hours" as const };
    return duration;
};

export const BoundaryTimerNodePanel = (props: EventPanelProps) => {
    const { t } = useTranslation();
    const { selection, modeler, data, canViewAdvanced, readOnly, onChanged } = props;
    const kind = panelNodeKindFor(selection);
    const binding = primaryBindingFor(selection.businessObject);
    const [duration, setDuration] = useState<DurationValue>(boundaryTimerDurationFor(binding));
    const [businessHours, setBusinessHours] = useState(String(binding?.businessHours) === "true");
    const [scheduleId, setScheduleId] = useState(String(binding?.scheduleId || ""));
    const [interruptive, setInterruptive] = useState(selection.businessObject?.cancelActivity !== false);
    const [disposition, setDisposition] = useState(String(binding?.workRecordDisposition || "cancel"));
    const schedules = data.schedules;

    useEffect(() => {
        const current = primaryBindingFor(selection.businessObject);
        setDuration(boundaryTimerDurationFor(current));
        setBusinessHours(String(current?.businessHours) === "true");
        setScheduleId(String(current?.scheduleId || ""));
        setInterruptive(selection.businessObject?.cancelActivity !== false);
        setDisposition(String(current?.workRecordDisposition || "cancel"));
    }, [selection.id, selection.businessObject]);

    const change = (updates: Record<string, unknown>) => {
        updateBinding(selection, modeler, "timer", updates);
        onChanged();
    };

    return (
        <FlussNodePanelShell
            selection={selection}
            kind={kind}
            canViewAdvanced={canViewAdvanced}
            tabs={[{
                key: "boundary_timer",
                label: t("fluss.node_panel.tab.timer", { defaultValue: "Timer" }),
                content: (
                    <Stack spacing={1.25}>
                        <FlussNodePanelField
                            label={t("fluss.node_panel.boundary_timer.deadline", { defaultValue: "Deadline" })}
                        >
                            <DurationInput
                                value={duration}
                                disabled={readOnly}
                                allowBusinessHours
                                onChange={(next) => {
                                    setDuration(next);
                                    const nextBusinessHours = next.unit === "business_hours";
                                    setBusinessHours(nextBusinessHours);
                                    change({
                                        mode: "duration",
                                        durationMinutes: durationToMinutes(next.amount, next.unit),
                                        businessHours: nextBusinessHours ? "true" : "",
                                        scheduleId: nextBusinessHours ? scheduleId : "",
                                    });
                                }}
                            />
                        </FlussNodePanelField>
                        {businessHours && (
                            <FlussNodePanelField
                                label={t("fluss.node_panel.boundary_timer.schedule", {
                                    defaultValue: "Business hours calendar",
                                })}
                            >
                                <Autocomplete
                                    size="small"
                                    disabled={readOnly}
                                    options={schedules}
                                    getOptionLabel={(option) => option.name || ""}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    value={schedules.find((option) => option.id === scheduleId) || null}
                                    onChange={(_, value) => {
                                        const next = value?.id || "";
                                        setScheduleId(next);
                                        change({ scheduleId: next });
                                    }}
                                    renderInput={(params) => <TextField {...params} />}
                                />
                            </FlussNodePanelField>
                        )}
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={interruptive}
                                    disabled={readOnly}
                                    onChange={(event) => {
                                        setInterruptive(event.target.checked);
                                        modeler?.get("modeling")?.updateProperties(selection.element, {
                                            cancelActivity: event.target.checked,
                                        });
                                        onChanged();
                                    }}
                                />
                            )}
                            label={t("fluss.node_panel.boundary_timer.interrupt", {
                                defaultValue: "Interrupt the activity when it expires",
                            })}
                        />
                        <FlussNodePanelField
                            label={t("fluss.node_panel.boundary_timer.work_record", { defaultValue: "Open work record" })}
                        >
                            <Select
                                size="small"
                                value={disposition}
                                disabled={readOnly}
                                inputProps={{
                                    "aria-label": String(t("fluss.node_panel.boundary_timer.work_record", {
                                        defaultValue: "Open work record",
                                    })),
                                }}
                                onChange={(event) => {
                                    setDisposition(event.target.value);
                                    change({ workRecordDisposition: event.target.value });
                                }}
                            >
                                <MenuItem value="cancel">
                                    {t("fluss.node_panel.boundary_timer.work_record.cancel", {
                                        defaultValue: "Cancel the record",
                                    })}
                                </MenuItem>
                                <MenuItem value="keep">
                                    {t("fluss.node_panel.boundary_timer.work_record.keep", {
                                        defaultValue: "Keep it open and unlink",
                                    })}
                                </MenuItem>
                            </Select>
                        </FlussNodePanelField>
                        <Typography variant="caption" color="text.secondary">
                            {t("fluss.node_panel.boundary_timer.help", {
                                defaultValue: "This deadline applies only to this node and does not replace the table's SLA, which keeps running independently. Minimum resolution: 1 minute.",
                            })}
                        </Typography>
                    </Stack>
                ),
            }]}
        />
    );
};

const parseCondition = (raw: unknown): LogicalNode | null => {
    if (!raw) return null;
    if (typeof raw === "object") return raw as LogicalNode;
    try {
        return JSON.parse(String(raw)) as LogicalNode;
    } catch (_error) {
        return null;
    }
};

const serializedCondition = (value: LogicalNode | string) =>
    typeof value === "string" ? value : JSON.stringify(value);

// PRIM-029 (round 8, Bloco E): the engine side (Fluss::ConditionalBoundaryBinding,
// Fluss::ConditionalBoundaryDispatcher) already re-evaluates this condition on every commit to
// Condition waits observe the record selected by recordSource.
export const BoundaryConditionalNodePanel = (props: EventPanelProps) => {
    const { t } = useTranslation();
    const { selection, modeler, data, canViewAdvanced, readOnly, onChanged } = props;
    const kind = panelNodeKindFor(selection);
    const binding = primaryBindingFor(selection.businessObject);
    const catalog = useDataCatalogContext();
    const [recordSource, setRecordSource] = useState(String(binding?.recordSource || ""));
    const sourceNode = catalog.nodes.find((node) => node.outputs.some((output) => output.path === recordSource));
    const recordSourceLabel = sourceNode?.node_label.replace(/\s+\([^)]*\)$/, "");
    const [tableName, setTableName] = useState(String(resolveAnchorTable(modeler) || ""));
    const [condition, setCondition] = useState<LogicalNode | null>(parseCondition(binding?.condition));

    useEffect(() => {
        const current = primaryBindingFor(selection.businessObject);
        setTableName(String(resolveAnchorTable(modeler) || ""));
        setRecordSource(String(current?.recordSource || ""));
        setCondition(parseCondition(current?.condition));
    }, [selection.id, selection.businessObject, modeler, data.tables]);

    const change = (updates: Record<string, unknown>) => {
        updateBinding(selection, modeler, "conditional", updates);
        onChanged();
    };

    return (
        <FlussNodePanelShell
            selection={selection}
            kind={kind}
            canViewAdvanced={canViewAdvanced}
            tabs={[{
                key: "boundary_conditional",
                label: t("fluss.node_panel.tab.condition", { defaultValue: "Condition" }),
                content: (
                    <Stack spacing={1.25}>
                        <RecordSourceBindingField selection={selection} modeler={modeler} kind="conditional" readOnly={readOnly} onChanged={onChanged}
                            onResolved={(info) => setTableName(info.referencedTable || "")}
                            onSelected={(value) => setRecordSource(value)} />
                        <FlussNodePanelField
                            label={t("fluss.node_panel.boundary_conditional.condition", { defaultValue: "Wait until" })}
                            fieldPath="condition" issuePrefix
                        >
                            {/* No `nodeOutputVariables` here, deliberately: Fluss::ConditionalBoundaryDispatcher only
                                re-evaluates this condition on a commit to the OBSERVED record (Fluss::RecordCallbacks'
                                after_commit), matched against that record's own columns
                                (Fluss::RecordConditionGrammar.match?) -- a "var.<node>.<key>" process variable can
                                never be true there, so a token waiting on one hangs forever with no error. The
                                compiler now rejects this at publish time too (Fluss::ConditionalBoundaryBinding.
                                validation_issues), but not offering the field here is what stops an author from
                                building it in the first place. */}
                            <FlussConditionBuilder
                                value={condition}
                                recordTable={tableName || undefined}
                                recordSourceLabel={recordSourceLabel}
                                recordOnly
                                onChange={(next) => {
                                    const parsed = typeof next === "string" ? parseCondition(next) : next;
                                    setCondition(parsed);
                                    change({ condition: serializedCondition(next) });
                                }}
                            />
                        </FlussNodePanelField>
                        <Typography variant="caption" color="text.secondary">
                            {t("fluss.node_panel.boundary_conditional.help", {
                                defaultValue: "O processo volta a andar quando o registro observado atender à condição enquanto esta etapa estiver ativa.",
                            })}
                        </Typography>
                    </Stack>
                ),
            }]}
        />
    );
};

export const BoundaryErrorNodePanel = (props: EventPanelProps) => {
    const { t } = useTranslation();
    const { selection, modeler, canViewAdvanced, readOnly, onChanged } = props;
    const kind = panelNodeKindFor(selection);
    const binding = primaryBindingFor(selection.businessObject);
    const rawCodes = String(binding?.errorCodes || "any");
    const [anyError, setAnyError] = useState(rawCodes === "any" || rawCodes === "");
    const [codes, setCodes] = useState<string[]>(
        rawCodes === "any" || rawCodes === "" ? [] : rawCodes.split(",").map((code) => code.trim()).filter(Boolean)
    );
    const [variable, setVariable] = useState(String(binding?.errorVariable || ""));

    useEffect(() => {
        const current = primaryBindingFor(selection.businessObject);
        const currentRaw = String(current?.errorCodes || "any");
        setAnyError(currentRaw === "any" || currentRaw === "");
        setCodes(currentRaw === "any" || currentRaw === "" ? [] : currentRaw.split(",").map((code) => code.trim()).filter(Boolean));
        setVariable(String(current?.errorVariable || ""));
    }, [selection.id, selection.businessObject]);

    const change = (updates: Record<string, unknown>) => {
        updateBinding(selection, modeler, "error", updates);
        onChanged();
    };

    return (
        <FlussNodePanelShell
            selection={selection}
            kind={kind}
            canViewAdvanced={canViewAdvanced}
            tabs={[{
                key: "boundary_error",
                label: t("fluss.node_panel.tab.error", { defaultValue: "Error" }),
                content: (
                    <Stack spacing={1.25}>
                        <FlussNodePanelField
                            label={t("fluss.node_panel.boundary_error.captured", { defaultValue: "Captured errors" })}
                        >
                            <Select
                                size="small"
                                value={anyError ? "any" : "specific"}
                                disabled={readOnly}
                                inputProps={{
                                    "aria-label": String(t("fluss.node_panel.boundary_error.captured", {
                                        defaultValue: "Captured errors",
                                    })),
                                }}
                                onChange={(event) => {
                                    const next = event.target.value === "any";
                                    setAnyError(next);
                                    change({ errorCodes: next ? "any" : codes.join(",") });
                                }}
                            >
                                <MenuItem value="any">
                                    {t("fluss.node_panel.boundary_error.any", { defaultValue: "Any error" })}
                                </MenuItem>
                                <MenuItem value="specific">
                                    {t("fluss.node_panel.boundary_error.specific", { defaultValue: "Specific codes" })}
                                </MenuItem>
                            </Select>
                        </FlussNodePanelField>
                        {!anyError && (
                            <Autocomplete
                                multiple
                                freeSolo
                                size="small"
                                disabled={readOnly}
                                options={[]}
                                value={codes}
                                renderTags={(value, getTagProps) => value.map((option, index) => (
                                    <Chip
                                        variant="outlined"
                                        size="small"
                                        label={option}
                                        {...getTagProps({ index })}
                                        key={option}
                                    />
                                ))}
                                onChange={(_, value) => {
                                    const next = value.map((entry) => String(entry).trim()).filter(Boolean);
                                    setCodes(next);
                                    change({ errorCodes: next.join(",") });
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder={String(t("fluss.node_panel.boundary_error.codes_placeholder", {
                                            defaultValue: "Type a code and press Enter",
                                        }))}
                                    />
                                )}
                            />
                        )}
                        <FlussNodePanelField
                            label={t("fluss.node_panel.boundary_error.variable", { defaultValue: "Store details in" })}
                            hint={t("fluss.node_panel.boundary_error.variable_hint", { defaultValue: "Informe o nome da variável que guardará os detalhes do erro, por exemplo, erro_pagamento. Use-a nas próximas etapas para consultar o código e, quando disponível, o motivo do erro." })}
                        >
                            <TextField
                                size="small"
                                value={variable}
                                disabled={readOnly}
                                inputProps={{
                                    "aria-label": String(t("fluss.node_panel.boundary_error.variable", {
                                        defaultValue: "Store details in",
                                    })),
                                }}
                                onChange={(event) => {
                                    setVariable(event.target.value);
                                    change({ errorVariable: event.target.value });
                                }}
                            />
                        </FlussNodePanelField>
                    </Stack>
                ),
            }]}
        />
    );
};
